import { useState, useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FileUpload } from "@/components/FileUpload";
import { parseAmountDigits, formatAmountInputDisplay } from "@/lib/format";
import {
  getRegions,
  getDistricts,
  getMfys,
  getSickCategories,
  getTypeHelps,
  getSubmitterTypes,
} from "@/api/manual.api";
import { createFamily, type FamilyFile } from "@/api/family.api";
import type { SelectItem } from "@/types/manual.types";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from "@/components/ui/drawer";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  BackHeader,
  Card,
  Field,
  TextField,
  TextArea,
  SelectField,
  Checkbox,
  Button,
  Icon,
  ICONS,
} from "@/components/kit";

interface CreateApplicationPageProps {
  onBack: () => void;
  /** Muvaffaqiyat oynasidan — "Mening arizalarim" bo'limiga o'tish */
  onViewApplications: () => void;
  /** Muvaffaqiyat oynasidan — bosh sahifaga (Home tab) qaytish */
  onGoHome: () => void;
}

interface FormState {
  pinfl: string;
  birthDate: string;
  regionId: number;
  districtId: number;
  mfyId: number;
  patientName: string;
  patientSurname: string;
  phoneNumber: string;
  amount: string;
  mySelf: boolean;
  sickCategoryId: number;
  sickCotegoryComment: string;
  comment: string;
  typeHelpId: string;
  applicationSubmitterTypeId: string;
}

const INIT_FORM: FormState = {
  pinfl: "",
  birthDate: "",
  regionId: 0,
  districtId: 0,
  mfyId: 0,
  patientName: "",
  patientSurname: "",
  phoneNumber: "+998",
  amount: "",
  mySelf: false,
  sickCategoryId: 0,
  sickCotegoryComment: "",
  comment: "",
  typeHelpId: "",
  applicationSubmitterTypeId: "",
};

/** HTML input[type=date] max — mahalliy sana (UTC shift xatosiz) */
function todayIsoLocal(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/* ── Form section: sarlavha + Card ── */
function FormSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h2 className="mb-2 px-1 text-base font-bold" style={{ color: "var(--c-text)" }}>
        {title}
      </h2>
      <Card className="flex flex-col gap-4 p-4">{children}</Card>
    </div>
  );
}

/* ── Select adapteri (kit SelectField + SelectItem ro'yxati) ── */
function SelectList({
  label,
  required,
  error,
  value,
  items,
  loading,
  disabled,
  placeholder,
  onChange,
}: {
  label: string;
  required?: boolean;
  error?: string;
  value: number | string;
  items: SelectItem[];
  loading?: boolean;
  disabled?: boolean;
  placeholder: string;
  onChange: (v: string) => void;
}) {
  return (
    <Field label={label} required={required} error={error}>
      <SelectField
        value={value === 0 ? "" : value}
        invalid={!!error}
        disabled={disabled || loading}
        placeholder={loading ? "..." : placeholder}
        onChange={(e) => onChange(e.target.value)}
      >
        {items.map((it) => (
          <option key={it.value} value={it.value}>
            {it.text}
          </option>
        ))}
      </SelectField>
    </Field>
  );
}

/** Ariza muvaffaqiyatli yuborilgandan keyingi tasdiq oynasi (Figma). */
function SuccessDialog({
  open,
  onViewApplications,
  onGoHome,
}: {
  open: boolean;
  onViewApplications: () => void;
  onGoHome: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={open}>
      <DialogContent
        showCloseButton={false}
        className="rounded-3xl border-0 p-6"
        style={{ background: "var(--c-surface)" }}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <div className="flex flex-col items-center text-center">
          <span style={{ color: "var(--c-success)" }}>
            <Icon name={ICONS.success} size={56} />
          </span>
          <DialogTitle className="mt-4 text-xl font-bold" style={{ color: "var(--c-text)" }}>
            {t("formSuccess")}
          </DialogTitle>
          <DialogDescription className="mt-2 text-sm leading-relaxed" style={{ color: "var(--c-text-muted)" }}>
            {t("formSuccessDesc", { section: t("myFeesTitle") })}
          </DialogDescription>
        </div>

        <div className="mt-6 flex flex-col gap-2.5">
          <Button variant="tint" fullWidth onClick={onViewApplications}>
            {t("formGoToApplications")}
          </Button>
          <Button variant="secondary" fullWidth onClick={onGoHome}>
            {t("formGoHome")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CreateApplicationPage({ onBack, onViewApplications, onGoHome }: CreateApplicationPageProps) {
  const { t } = useTranslation();

  const [form, setForm] = useState<FormState>(INIT_FORM);
  const [files, setFiles] = useState<FamilyFile[]>([]);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [offerAccepted, setOfferAccepted] = useState(false);
  const [offerError, setOfferError] = useState<string | null>(null);
  const [showOfferModal, setShowOfferModal] = useState(false);

  /* ── Select lists ── */
  const [regions, setRegions] = useState<SelectItem[]>([]);
  const [districts, setDistricts] = useState<SelectItem[]>([]);
  const [mfys, setMfys] = useState<SelectItem[]>([]);
  const [sickCategories, setSickCategories] = useState<SelectItem[]>([]);
  const [typeHelps, setTypeHelps] = useState<SelectItem[]>([]);
  const [submitterTypes, setSubmitterTypes] = useState<SelectItem[]>([]);

  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingMfys, setLoadingMfys] = useState(false);

  useEffect(() => {
    getRegions().then(setRegions).catch(() => { });
    getSickCategories().then(setSickCategories).catch(() => { });
    getTypeHelps().then(setTypeHelps).catch(() => { });
    getSubmitterTypes().then(setSubmitterTypes).catch(() => { });
  }, []);

  useEffect(() => {
    if (!form.regionId) {
      setDistricts([]);
      return;
    }
    setLoadingDistricts(true);
    getDistricts(form.regionId)
      .then(setDistricts)
      .catch(() => setDistricts([]))
      .finally(() => setLoadingDistricts(false));
  }, [form.regionId]);

  useEffect(() => {
    if (!form.districtId) {
      setMfys([]);
      return;
    }
    setLoadingMfys(true);
    getMfys(form.districtId)
      .then(setMfys)
      .catch(() => setMfys([]))
      .finally(() => setLoadingMfys(false));
  }, [form.districtId]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const validate = (): boolean => {
    const req = t("formRequired");
    const errs: Partial<Record<keyof FormState, string>> = {};
    if (!form.pinfl || form.pinfl.length !== 14) errs.pinfl = req;
    if (!form.birthDate) errs.birthDate = req;
    else {
      const d = new Date(`${form.birthDate}T12:00:00`);
      const endToday = new Date();
      endToday.setHours(23, 59, 59, 999);
      if (Number.isNaN(d.getTime()) || d > endToday) errs.birthDate = t("formBirthDateInvalid");
    }
    if (!form.regionId) errs.regionId = req;
    if (!form.districtId) errs.districtId = req;
    if (!form.mfyId) errs.mfyId = req;
    if (!form.patientName.trim()) errs.patientName = req;
    if (!form.patientSurname.trim()) errs.patientSurname = req;
    if (form.phoneNumber.length < 13) errs.phoneNumber = req;
    if (!form.amount || Number(form.amount) <= 0) errs.amount = req;
    if (!form.sickCategoryId) errs.sickCategoryId = req;
    if (!form.typeHelpId) errs.typeHelpId = req;
    if (!form.applicationSubmitterTypeId) errs.applicationSubmitterTypeId = req;
    setErrors(errs);
    if (!offerAccepted) setOfferError(req);
    else setOfferError(null);
    return Object.keys(errs).length === 0 && offerAccepted;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await createFamily({
        pinfl: form.pinfl,
        birthDate: form.birthDate,
        regionId: form.regionId,
        districtId: form.districtId,
        mfyId: form.mfyId,
        patientName: form.patientName,
        patientSurname: form.patientSurname,
        phoneNumber: form.phoneNumber,
        amount: Number(form.amount),
        mySelf: form.mySelf,
        sickCategoryId: form.sickCategoryId,
        sickCotegoryComment: form.sickCotegoryComment?.length > 0 ? form.sickCotegoryComment : null,
        comment: form.comment?.length > 0 ? form.comment : null,
        typeHelpId: Number(form.typeHelpId),
        applicationSubmitterTypeId: Number(form.applicationSubmitterTypeId),
        files,
      });
      setSubmitted(true);
    } catch (e) {
      setSubmitError((e as Error).message ?? t("errorLoading"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--app-bg)" }}>
      <div className="mx-auto max-w-lg">
        <BackHeader title={t("formTitle")} onBack={onBack} />

        <div className="flex flex-col gap-5 px-4 pb-6 pt-2">
          {/* Bemor ma'lumotlari */}
          <FormSection title={t("formSecPatient")}>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("formPatientName")} required error={errors.patientName}>
                <TextField
                  placeholder={t("formPatientName")}
                  invalid={!!errors.patientName}
                  value={form.patientName}
                  onChange={(e) => set("patientName", e.target.value)}
                />
              </Field>
              <Field label={t("formPatientSurname")} required error={errors.patientSurname}>
                <TextField
                  placeholder={t("formPatientSurname")}
                  invalid={!!errors.patientSurname}
                  value={form.patientSurname}
                  onChange={(e) => set("patientSurname", e.target.value)}
                />
              </Field>
            </div>
            <Field label={t("formPinfl")} required error={errors.pinfl}>
              <TextField
                inputMode="numeric"
                maxLength={14}
                placeholder={t("formPinflPlaceholder")}
                invalid={!!errors.pinfl}
                value={form.pinfl}
                onChange={(e) => set("pinfl", e.target.value.replace(/\D/g, ""))}
              />
            </Field>
            <Field label={t("formBirthDate")} required error={errors.birthDate}>
              <TextField
                type="date"
                max={todayIsoLocal()}
                min="1900-01-01"
                autoComplete="bday"
                invalid={!!errors.birthDate}
                value={form.birthDate}
                onChange={(e) => set("birthDate", e.target.value)}
                className={form.birthDate ? undefined : "font-normal"}
                style={{ color: form.birthDate ? "var(--c-text)" : "var(--c-text-muted)" }}
              />
            </Field>
          </FormSection>

          {/* Aloqa */}
          <FormSection title={t("formSecContact")}>
            <Field label={t("formPhone")} required error={errors.phoneNumber}>
              <div
                className="flex h-[52px] overflow-hidden rounded-xl"
                style={{ background: "var(--c-surface)", boxShadow: `inset 0 0 0 1px ${errors.phoneNumber ? "var(--c-danger)" : "var(--c-line-input)"}` }}
              >
                <span className="flex items-center px-4 text-base font-semibold" style={{ color: "var(--c-text-muted)", borderRight: "1px solid var(--c-line)" }}>
                  +998
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={9}
                  placeholder="XX XXX XX XX"
                  className="flex-1 bg-transparent px-4 text-base font-semibold outline-none"
                  style={{ color: "var(--c-text)" }}
                  value={form.phoneNumber.replace("+998", "")}
                  onChange={(e) => set("phoneNumber", "+998" + e.target.value.replace(/\D/g, "").slice(0, 9))}
                />
              </div>
            </Field>
          </FormSection>

          {/* Manzil */}
          <FormSection title={t("formSecAddress")}>
            <SelectList
              label={t("formRegion")}
              required
              error={errors.regionId}
              value={form.regionId}
              items={regions}
              placeholder={t("selectPlaceholder")}
              onChange={(v) => set("regionId", Number(v))}
            />
            <SelectList
              label={t("formDistrict")}
              required
              error={errors.districtId}
              value={form.districtId}
              items={districts}
              loading={loadingDistricts}
              disabled={!form.regionId}
              placeholder={t("selectPlaceholder")}
              onChange={(v) => {
                set("districtId", Number(v));
                set("mfyId", 0);
              }}
            />
            <SelectList
              label={t("formMfy")}
              required
              error={errors.mfyId}
              value={form.mfyId}
              items={mfys}
              loading={loadingMfys}
              disabled={!form.districtId}
              placeholder={t("selectPlaceholder")}
              onChange={(v) => set("mfyId", Number(v))}
            />
          </FormSection>

          {/* Tibbiy ma'lumotlar */}
          <FormSection title={t("formSecMedical")}>
            <SelectList
              label={t("formTypeHelp")}
              required
              error={errors.typeHelpId}
              value={form.typeHelpId}
              items={typeHelps}
              placeholder={t("selectPlaceholder")}
              onChange={(v) => set("typeHelpId", v)}
            />
            <SelectList
              label={t("formSickCategory")}
              required
              error={errors.sickCategoryId}
              value={form.sickCategoryId}
              items={sickCategories}
              placeholder={t("selectPlaceholder")}
              onChange={(v) => set("sickCategoryId", Number(v))}
            />
            {form.sickCategoryId === 33 && (
              <Field label={t("formSickComment")}>
                <TextArea
                  rows={2}
                  placeholder={t("formSickComment") + "..."}
                  value={form.sickCotegoryComment}
                  onChange={(e) => set("sickCotegoryComment", e.target.value)}
                />
              </Field>
            )}
          </FormSection>

          {/* Kerakli mablag' */}
          <FormSection title={t("formSecAmount")}>
            <Field label={t("formAmount")} required error={errors.amount}>
              <TextField
                inputMode="numeric"
                autoComplete="off"
                placeholder={t("formAmountPlaceholder")}
                trailing={t("sumShort")}
                invalid={!!errors.amount}
                value={formatAmountInputDisplay(form.amount)}
                onChange={(e) => set("amount", parseAmountDigits(e.target.value))}
              />
            </Field>
          </FormSection>

          {/* Boshqa */}
          <FormSection title={t("formSecOther")}>
            <SelectList
              label={t("formSubmitterType")}
              required
              error={errors.applicationSubmitterTypeId}
              value={form.applicationSubmitterTypeId}
              items={submitterTypes}
              placeholder={t("selectPlaceholder")}
              onChange={(v) => set("applicationSubmitterTypeId", v)}
            />
            <Field label={t("formComment")}>
              <TextArea
                rows={3}
                placeholder={t("formComment") + "..."}
                value={form.comment}
                onChange={(e) => set("comment", e.target.value)}
              />
            </Field>
            <Field label={t("formFiles")}>
              <FileUpload mode="multiple" onChange={setFiles} />
            </Field>
          </FormSection>

          {/* Offerta */}
          <div>
            <Checkbox checked={offerAccepted} onChange={(v) => { setOfferAccepted(v); if (v) setOfferError(null); }}>
              {t("formOfferConsentPrefix", { submitLabel: t("formSubmit") })}{" "}
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); setShowOfferModal(true); }}
                className="underline underline-offset-2"
                style={{ color: "var(--primary)" }}
              >
                {t("formOfferPersonalData")}
              </button>{" "}
              {t("formOfferConsentSuffix")}
            </Checkbox>
            {offerError && <p className="mt-2 text-xs" style={{ color: "var(--c-danger)" }}>{offerError}</p>}
          </div>

          {submitError && (
            <div className="rounded-xl px-4 py-3 text-sm font-medium" style={{ background: "var(--c-danger-tint)", color: "var(--c-danger)" }}>
              {submitError}
            </div>
          )}
        </div>

        {/* Sticky submit */}
        <div
          className="sticky bottom-0 z-10 px-4 pt-3"
          style={{
            background: "color-mix(in srgb, var(--app-bg) 92%, transparent)",
            backdropFilter: "blur(8px)",
            borderTop: "1px solid var(--c-line)",
            paddingBottom: "max(env(safe-area-inset-bottom), 12px)",
          }}
        >
          <Button size="cta" fullWidth loading={isSubmitting} onClick={handleSubmit}>
            {t("formSubmit")}
          </Button>
        </div>
      </div>

      <Drawer open={showOfferModal} onOpenChange={setShowOfferModal}>
        <DrawerContent
          className="mx-auto w-full max-w-2xl"
          style={{ background: "var(--c-surface)" }}
        >
          <DrawerHeader className="border-b" style={{ borderColor: "var(--c-line)" }}>
            <div className="flex items-center justify-between">
              <DrawerTitle className="text-base font-semibold" style={{ color: "var(--c-text)" }}>
                Ommaviy oferta
              </DrawerTitle>
              <DrawerClose asChild>
                <button
                  type="button"
                  className="grid size-8 place-items-center rounded-full"
                  style={{ background: "var(--c-elevated-2)", color: "var(--c-text)" }}
                  aria-label={t("back")}
                >
                  <Icon name={ICONS.close} size={20} />
                </button>
              </DrawerClose>
            </div>
          </DrawerHeader>
          <div
            className="overflow-y-auto p-4"
            style={{ maxHeight: "min(70vh, calc(100dvh - 110px))", overscrollBehavior: "contain", WebkitOverflowScrolling: "touch" }}
          >
            <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: "var(--c-text)" }}>
              {t("offertaText")}
            </p>
          </div>
        </DrawerContent>
      </Drawer>

      <SuccessDialog open={submitted} onViewApplications={onViewApplications} onGoHome={onGoHome} />
    </div>
  );
}
